import React, { memo, useMemo, useState } from "react";
import { Box, Button, FormControlLabel, Popover, SelectChangeEvent } from "@mui/material";
import theme from "assets/styles/themes";
import { Checkbox } from "components/Checkbox";
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";
import { getYearsList } from "utils";
import { YearPicker } from "./components/YearPicker";
import { FormattedMessage } from "react-intl";

const CURRENT_YEAR = new Date().getFullYear();

interface DateRangeProps {
    open: boolean;
    startDateLimit: number;
    dates: {
        startDate: string;
        endDate: string;
        present: boolean;
    };
    handleToggle: (isOpen: boolean) => void;
    setDates: React.Dispatch<
        React.SetStateAction<{
            startDate: string;
            endDate: string;
            present: boolean;
        }>
    >;
}

const DateRange = memo(function DateRange({ open, startDateLimit, dates, handleToggle, setDates }: DateRangeProps) {
    const { startDate, endDate, present } = dates;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const startDateOptions = useMemo(
        () => getYearsList(startDateLimit, Number(endDate) || CURRENT_YEAR),
        [endDate, startDateLimit]
    );
    const endDateOptions = useMemo(
        () => getYearsList(Number(startDate) || startDateLimit),
        [startDate, startDateLimit]
    );

    const dateRangeLabel =
        startDate && endDate ? `${startDate} - ${endDate}` : <FormattedMessage id="tenureChart.dateRange.label" />;

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        handleToggle(true);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        handleToggle(false);
        setAnchorEl(null);
    };

    const handleChangeStartDate = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
    ) => {
        setDates((prevVal) => ({ ...prevVal, startDate: event.target.value }));
    };

    const handleChangeEndDate = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
    ) => {
        const isSameYear = event.target.value !== CURRENT_YEAR.toString();
        setDates((prevVal) => ({ ...prevVal, endDate: event.target.value, present: isSameYear ? false : true }));
    };

    const handleCheckPresent = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.checked;
        setDates((prevVal) => ({
            ...prevVal,
            present: value,
            endDate: value ? CURRENT_YEAR.toString() : prevVal.endDate,
        }));
    };

    return (
        <div>
            <Button
                variant="outlined"
                endIcon={<CustomIcon icon={icons.chevronDownThin} sx={{ color: theme.palette.action.active }} />}
                onClick={handleOpen}
                sx={{
                    minWidth: 156,
                    height: 32,
                    padding: "8px 16px",
                    justifyContent: "space-between",
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.neutrals.dark}`,
                    "& .MuiButton-endIcon": {
                        marginRight: 0,
                    },
                    "&:hover": {
                        fontWeight: 400,
                        color: theme.palette.primary.main,
                    },
                }}
            >
                {dateRangeLabel}
            </Button>
            {open && (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            borderRadius: "6px",
                            boxShadow: "0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)",
                        },
                    }}
                >
                    <Box sx={{ width: 296, height: 92, padding: "8px 16px", display: "flex", alignItems: "top" }}>
                        <YearPicker
                            label="Start Date"
                            date={startDate}
                            setDate={handleChangeStartDate}
                            optionsMessage={`Data available from Jan ${startDateLimit} onwards.`}
                            options={startDateOptions}
                            sx={{ mr: 1 }}
                        />
                        <Box>
                            <YearPicker
                                label="End Date"
                                date={endDate}
                                setDate={handleChangeEndDate}
                                options={endDateOptions}
                            />
                            <FormControlLabel
                                sx={{
                                    mr: 0,
                                    ml: 0,
                                    "& .MuiTypography-root": {
                                        fontSize: 9,
                                    },
                                    "& .MuiCheckbox-root": {
                                        padding: 0,
                                        mr: 0.5,
                                    },
                                }}
                                control={<Checkbox checked={present} onChange={handleCheckPresent} />}
                                label="Present"
                            />
                        </Box>
                    </Box>
                </Popover>
            )}
        </div>
    );
});

export default DateRange;
